/**
 * AuthContext – v16 (Clean Rewrite – Single Init Flow)
 *
 * ROOT CAUSE của auth cũ bị treo / race condition:
 * ──────────────────────────────────────────────────
 * 1. Race condition getSession() vs INITIAL_SESSION:
 *    Cả hai đều gọi handleAuthUser() → double profile fetch, double setState.
 *    Guard initialSessionHandled không đủ vì INITIAL_SESSION có thể fire
 *    TRƯỚC khi getSession() resolve → cả hai đều chạy.
 *
 * 2. Safety timer 4s không clear đúng:
 *    clearTimeout() chỉ trong .then() của safeGetSession() → nếu getSession
 *    chậm hơn 4s, timer fire trước → isLoading=false sớm → ProtectedRoute
 *    render với profile=null → FullScreenLoader lại → vòng lặp.
 *
 * 3. handleAuthUser async trong onAuthStateChange switch:
 *    await trong listener → nếu component unmount giữa chừng → state update
 *    sau unmount → React warning + potential infinite re-render.
 *
 * 4. detectSessionInUrl=true trên mọi trang:
 *    Supabase parse URL trước khi fire INITIAL_SESSION → delay/treo trên
 *    trang không phải OAuth callback.
 *
 * 5. ProtectedRoute case "authenticated + no profile":
 *    Nếu profile fetch fail và safety timer đã fire → isLoading=false nhưng
 *    profile=null → ProtectedRoute stuck ở FullScreenLoader mãi.
 *
 * KIẾN TRÚC MỚI v16:
 * ──────────────────
 * - CHỈ dùng onAuthStateChange để init VÀ sync.
 *   INITIAL_SESSION là event đầu tiên, luôn fire khi mount → xử lý ở đây.
 *   Không cần getSession() riêng → không race condition.
 *
 * - Safety timer chỉ set 1 lần, clear ngay khi INITIAL_SESSION fire.
 *   Không bao giờ để timer fire sau khi đã có session.
 *
 * - handleAuthUser KHÔNG async trong listener.
 *   Dùng pattern: listener gọi handleAuthUser() không await,
 *   handleAuthUser tự quản lý mounted check.
 *
 * - Profile fail → set authError rõ ràng → ProtectedRoute redirect login.
 *   Không treo vô hạn.
 *
 * - Invalid Refresh Token → clearInvalidSession() → redirect login.
 *
 * FLOW:
 * ─────
 * App mount → onAuthStateChange subscribe
 *   → INITIAL_SESSION (luôn fire đầu tiên):
 *     → có session → handleAuthUser() → fetchProfile → setProfile → isLoading=false
 *     → không có session → clear state → isLoading=false
 *   → SIGNED_IN (sau login):
 *     → handleAuthUser() nếu user khác hoặc chưa có profile
 *   → TOKEN_REFRESHED:
 *     → update session/user, fetch profile nếu thiếu
 *   → SIGNED_OUT:
 *     → clear state
 *   → TOKEN_REFRESH_FAILED:
 *     → clearInvalidSession → redirect login
 *
 * Safety timer 5s: chỉ fallback nếu INITIAL_SESSION không fire (edge case)
 */
import {
  createContext, useContext, useEffect, useState,
  useCallback, useRef,
} from 'react';
import type { User, Session } from '@supabase/supabase-js';
import { supabase, AUTH_STORAGE_KEY } from '@/core/database/supabase';
import type { AuthContextValue, UserProfile } from './auth.types';
import type { NavigateFunction } from 'react-router-dom';
import type { UserRole } from '@/config/navigation.config';

// ── Constants ────────────────────────────────────────────────
/** Fallback timeout nếu INITIAL_SESSION không fire (edge case) */
const SAFETY_TIMEOUT_MS = 5_000;
/** Debounce visibility check để tránh spam getSession */
const VISIBILITY_DEBOUNCE_MS = 60_000;

// ── Navigate ref (injected từ router/index.ts) ───────────────
export const authNavigateRef = { current: null as NavigateFunction | null };

// ── Context ──────────────────────────────────────────────────
const AuthContext = createContext<AuthContextValue | null>(null);

// ── Role → default redirect map ──────────────────────────────
const ROLE_HOME_MAP: Partial<Record<UserRole, string>> = {
  staff: '/worker-dashboard',
  // Các role khác về '/' (home/page.tsx xử lý redirect tiếp theo role)
};

// ── Clear auth storage ───────────────────────────────────────
function clearAuthStorage(): void {
  try {
    const rememberEmail = localStorage.getItem('remember_email');
    const rememberPassword = localStorage.getItem('remember_password');
    localStorage.clear();
    sessionStorage.clear();
    if (rememberEmail) localStorage.setItem('remember_email', rememberEmail);
    if (rememberPassword) localStorage.setItem('remember_password', rememberPassword);
  } catch { /* ignore */ }
}

// ── AuthProvider ─────────────────────────────────────────────
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [originalProfile, setOriginalProfile] = useState<UserProfile | null>(null);

  const mountedRef = useRef(true);
  const profileRef = useRef<UserProfile | null>(null);
  const signingOut = useRef(false);
  const lastVisibilityCheck = useRef(0);
  // Track xem INITIAL_SESSION đã được xử lý chưa
  const initialHandled = useRef(false);

  // ── Fetch profile từ DB ──────────────────────────────────
  const fetchProfile = useCallback(async (userId: string): Promise<UserProfile | null> => {
    try {
      // 1. Lấy thông tin profile cơ bản
      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (profileError || !profileData) {
        if (profileError) console.error('[Auth] fetchProfile error:', profileError.message);
        return null;
      }

      const profile = profileData as UserProfile;

      // 2. Lấy tên vai trò (role_name) từ bảng roles
      if (profile.role_code) {
        const { data: roleData } = await supabase
          .from('roles')
          .select('name')
          .eq('code', profile.role_code)
          .maybeSingle();
        
        if (roleData) {
          profile.role_name = roleData.name;
        }
      }

      return profile;
    } catch (err) {
      console.error('[Auth] fetchProfile exception:', err);
      return null;
    }
  }, []);

  // ── Apply profile + cache ────────────────────────────────
  const applyProfile = useCallback((p: UserProfile) => {
    profileRef.current = p;
    setProfile(p);
    try {
      sessionStorage.setItem('lscm_cached_role', p.role_code);
      sessionStorage.setItem('lscm_cached_profile', JSON.stringify({
        id: p.id,
        fullName: p.full_name,
        email: p.email ?? '',
        phone: p.phone ?? undefined,
        avatarPath: p.avatar_path ?? undefined,
        roleCode: p.role_code,
        jobTitle: p.job_title ?? undefined,
        isActive: p.is_active,
      }));
    } catch { /* ignore */ }
  }, []);

  // ── Upsert profile cho user mới (first login / Google OAuth) ─
  const upsertProfileFromAuth = useCallback(async (authUser: User): Promise<UserProfile | null> => {
    const meta = authUser.user_metadata ?? {};
    const fullName: string =
      (meta['full_name'] as string) ||
      (meta['name'] as string) ||
      authUser.email?.split('@')[0] ||
      'Người dùng mới';
    const avatarPath: string | null =
      (meta['avatar_url'] as string) ||
      (meta['picture'] as string) ||
      null;

    try {
      const { error } = await supabase
        .from('user_profiles')
        .upsert(
          {
            id: authUser.id,
            full_name: fullName,
            email: authUser.email ?? null,
            avatar_path: avatarPath,
            role_code: 'staff', // default role thấp nhất
            job_title: 'Nhân viên bảo dưỡng',
            is_active: true,
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'id', ignoreDuplicates: true } // KHÔNG overwrite role_code đã có
        );
      if (error) {
        console.error('[Auth] upsertProfile error:', error.message);
      }
      return await fetchProfile(authUser.id);
    } catch (err) {
      console.error('[Auth] upsertProfile exception:', err);
      return null;
    }
  }, [fetchProfile]);

  // ── Handle authenticated user ────────────────────────────
  // Không async trong listener – được gọi fire-and-forget
  const handleAuthUser = useCallback((authUser: User, s: Session) => {
    if (!mountedRef.current || signingOut.current) return;

    setUser(authUser);
    setSession(s);
    setAuthError(null);

    // Nếu cùng user và đã có profile → chỉ update session, không fetch lại
    if (profileRef.current && profileRef.current.id === authUser.id) {
      setIsLoading(false);
      return;
    }

    // Fetch profile async (không block listener)
    const doFetch = async () => {
      let p = await fetchProfile(authUser.id);
      if (!mountedRef.current) return;

      // Profile chưa tồn tại → upsert (first login / Google OAuth)
      if (!p) {
        p = await upsertProfileFromAuth(authUser);
        if (!mountedRef.current) return;
      } else {
        // Fix lỗi DB trigger tự động gán quyền 'pm' cho user mới (đặc biệt khi login qua Google OAuth)
        const isNewUser = new Date().getTime() - new Date(authUser.created_at).getTime() < 60000; // < 1 phút
        if (p.role_code === 'pm' && isNewUser) {
          console.warn('[Auth] Detected new user with incorrect PM role, downgrading to staff');
          const { error: updateErr } = await supabase
            .from('user_profiles')
            .update({ role_code: 'staff' })
            .eq('id', authUser.id);
          if (!updateErr) {
            p.role_code = 'staff';
          }
        }
      }

      // Profile vẫn null → lỗi DB
      if (!p) {
        console.error('[Auth] Profile load failed for user:', authUser.id);
        setAuthError('Không thể tải thông tin tài khoản. Vui lòng đăng nhập lại.');
        setIsLoading(false);
        return;
      }

      // Kiểm tra chỉ nội bộ công ty mới được đăng nhập, trừ super_admin
      const email = authUser.email || '';
      const isInternalEmail = email.endsWith('@namlandscape.vn') || email.endsWith('@landscape.vn');
      if (p.role_code !== 'super_admin' && !isInternalEmail) {
        setAuthError('Hệ thống chỉ dành cho thành viên nội bộ công ty. Vui lòng sử dụng email đuôi @namlandscape.vn hoặc @landscape.vn');
        setIsLoading(false);
        supabase.auth.signOut({ scope: 'local' });
        return;
      }

      // Tài khoản bị vô hiệu hóa hoặc chờ duyệt
      const isDebugUser = authUser.email === 'debug@landscape.vn';
      if (!p.is_active && !isDebugUser) {
        setAuthError('Tài khoản của bạn đang chờ quản trị viên xét duyệt hoặc đã bị tạm khóa. Vui lòng liên hệ bộ phận hỗ trợ để được kích hoạt.');
        setIsLoading(false);
        return;
      }

      applyProfile(p);
      setIsLoading(false);
    };

    doFetch().catch((err) => {
      console.error('[Auth] handleAuthUser doFetch error:', err);
      if (mountedRef.current) {
        setAuthError('Lỗi xác thực. Vui lòng thử lại.');
        setIsLoading(false);
      }
    });
  }, [fetchProfile, upsertProfileFromAuth, applyProfile]);

  // ── Clear invalid session + redirect login ───────────────
  const clearInvalidSession = useCallback(() => {
    if (!mountedRef.current) return;
    console.warn('[Auth] Clearing invalid session, redirecting to login');
    clearAuthStorage();
    profileRef.current = null;
    setUser(null);
    setSession(null);
    setProfile(null);
    setAuthError(null);
    localStorage.removeItem('lscm_impersonated_id');
    setIsLoading(false);
    // Reset auth lock
    signingOut.current = false;

    // signOut local scope để clear Supabase internal state (bắt buộc)
    supabase.auth.signOut({ scope: 'local' }).catch(() => {});
    // Force page reload bằng href thay vì replace để chắc chắn trình duyệt load lại
    const base = (typeof __BASE_PATH__ !== 'undefined' ? __BASE_PATH__ : '/').replace(/\/$/, '');
    window.location.href = `${base}/auth/internal-login`;
  }, []);

  // ── Main init effect ─────────────────────────────────────
  useEffect(() => {
    mountedRef.current = true;

    // Safety timer: fallback nếu INITIAL_SESSION không fire (edge case)
    // Sẽ bị clear ngay khi INITIAL_SESSION fire
    const safetyTimer = setTimeout(() => {
      if (mountedRef.current && !initialHandled.current) {
        console.warn('[Auth] Safety timer fired – INITIAL_SESSION did not fire in time');
        setIsLoading(false);
      }
    }, SAFETY_TIMEOUT_MS);

    // ── onAuthStateChange: nguồn sự thật duy nhất ──────────
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, s) => {
        if (!mountedRef.current) return;

        console.debug('[Auth] event:', event, '| user:', s?.user?.id ?? 'none');

        switch (event) {
          case 'INITIAL_SESSION':
            // Luôn fire đầu tiên khi subscribe → đây là init point
            clearTimeout(safetyTimer);
            initialHandled.current = true;

            if (s?.user) {
              handleAuthUser(s.user, s);
            } else {
              // Không có session → clear state + thoát loading
              profileRef.current = null;
              setUser(null);
              setSession(null);
              setProfile(null);
              setIsLoading(false);
            }
            break;

          case 'SIGNED_IN':
          case 'USER_UPDATED':
            if (signingOut.current) break;
            if (s?.user) {
              handleAuthUser(s.user, s);
            }
            break;

          case 'TOKEN_REFRESHED':
            if (signingOut.current) break;
            if (s?.user) {
              setSession(s);
              setUser(s.user);
              // Fetch profile nếu chưa có (edge case sau refresh)
              if (!profileRef.current) {
                fetchProfile(s.user.id).then((p) => {
                  if (mountedRef.current && p) applyProfile(p);
                }).catch(() => {});
              }
            }
            break;

          case 'SIGNED_OUT':
            signingOut.current = false;
            profileRef.current = null;
            setUser(null);
            setSession(null);
            setProfile(null);
            setAuthError(null);
            setIsLoading(false);
            break;

          default:
            // TOKEN_REFRESH_FAILED
            if ((event as string) === 'TOKEN_REFRESH_FAILED') {
              console.error('[Auth] TOKEN_REFRESH_FAILED – clearing session');
              clearInvalidSession();
            }
            break;
        }
      }
    );

    // ── Catch unhandled refresh token errors ────────────────
    const handleUnhandledRejection = (ev: PromiseRejectionEvent) => {
      const msg = (ev.reason as Error)?.message ?? String(ev.reason ?? '');
      if (
        msg.includes('Invalid Refresh Token') ||
        msg.includes('Refresh Token Not Found') ||
        msg.includes('refresh_token_not_found')
      ) {
        console.error('[Auth] Unhandled refresh token error:', msg);
        ev.preventDefault();
        clearInvalidSession();
      }
    };
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    // ── Visibility change: re-check session khi tab active lại ─
    const handleVisibility = () => {
      if (!mountedRef.current || document.hidden) return;
      const now = Date.now();
      if (now - lastVisibilityCheck.current < VISIBILITY_DEBOUNCE_MS) return;
      lastVisibilityCheck.current = now;

      // Chỉ check nếu đang có user (tránh spam khi chưa login)
      if (!user) return;

      supabase.auth.getSession().then(({ data, error }) => {
        if (!mountedRef.current) return;
        if (error || !data.session) {
          // Session hết hạn khi tab ẩn
          if (!signingOut.current) {
            console.warn('[Auth] Session expired while tab was hidden');
            clearInvalidSession();
          }
        } else {
          setSession(data.session);
          setUser(data.session.user);
        }
      }).catch(() => {});
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      mountedRef.current = false;
      clearTimeout(safetyTimer);
      subscription.unsubscribe();
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── signIn ───────────────────────────────────────────────
  const signIn = useCallback(async (email: string, password: string): Promise<{ error: string | null }> => {
    setAuthError(null);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) return { error: error.message };

      // Log auth event (fire-and-forget)
      if (data.user) {
        supabase.rpc('log_auth_event', {
          p_user_id: data.user.id,
          p_action: 'login',
          p_summary: `Đăng nhập bằng email: ${email}`,
        }).then(({ error }) => {
          if (error) console.warn('[Auth] log_auth_event error:', error.message);
        });
      }
      return { error: null };
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Lỗi đăng nhập không xác định';
      return { error: msg };
    }
  }, []);

  // ── signInWithGoogle ─────────────────────────────────────
  const signInWithGoogle = useCallback(async (): Promise<{ error: string | null }> => {
    setAuthError(null);
    try {
      const basePath = typeof __BASE_PATH__ !== 'undefined' ? __BASE_PATH__ : '/';
      const callbackUrl = `${window.location.origin}${basePath.replace(/\/$/, '')}/auth/callback`;

      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: callbackUrl,
          queryParams: { access_type: 'offline', prompt: 'consent' },
        },
      });
      if (error) return { error: error.message };
      return { error: null };
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Lỗi kết nối Google';
      return { error: msg };
    }
  }, []);

  // ── signOut ──────────────────────────────────────────────
  const signOut = useCallback(async () => {
    if (signingOut.current) return;
    signingOut.current = true;

    try {
      if (user) {
        await supabase.rpc('log_auth_event', {
          p_user_id: user.id,
          p_action: 'logout',
          p_summary: `Đăng xuất: ${user.email}`,
        }).then(({ error }) => {
          if (error) console.warn('[Auth] log_auth_event logout error:', error.message);
        });
      }
      // Gửi yêu cầu revoke token lên server
      await supabase.auth.signOut();
    } catch {
      // Bỏ qua lỗi (mạng yếu, token đã hết hạn, v.v.)
    } finally {
      // Dù thành công hay thất bại, nuke toàn bộ storage để đảm bảo không bị dính token cũ
      localStorage.removeItem('lscm_impersonated_id');
      clearAuthStorage();

      // Ép trình duyệt tải lại trang (F5) để xóa sạch mọi cache trong RAM của Supabase và React
      const base = (typeof __BASE_PATH__ !== 'undefined' ? __BASE_PATH__ : '/').replace(/\/$/, '');
      window.location.href = `${base}/auth/internal-login`;
    }
  }, [user]);

  // ── refreshProfile ───────────────────────────────────────
  const refreshProfile = useCallback(async () => {
    if (!user?.id) return;
    const p = await fetchProfile(user.id);
    if (p && mountedRef.current) applyProfile(p);
  }, [user, fetchProfile, applyProfile]);

  // ── getRedirectPath ──────────────────────────────────────
  const getRedirectPath = useCallback((p: UserProfile, from?: string): string => {
    if (from && from !== '/auth/login' && from !== '/auth/register' && from !== '/auth/callback') {
      return from;
    }
    return ROLE_HOME_MAP[p.role_code as UserRole] ?? '/';
  }, []);

  // ── Impersonate ──────────────────────────────────────────
  const impersonateUser = useCallback(async (targetProfile: UserProfile) => {
    if (!profileRef.current || isImpersonating) return;
    
    // Lưu lại profile gốc
    setOriginalProfile(profileRef.current);
    setIsImpersonating(true);
    
    // Ghi đè profile hiện tại bằng profile mục tiêu
    applyProfile(targetProfile);
    localStorage.setItem('lscm_impersonated_id', targetProfile.id);
    
    // Log event (fire-and-forget)
    try {
      await supabase.rpc('log_auth_event', {
        p_user_id: user?.id,
        p_action: 'impersonate_start',
        p_summary: `Nhập vai người dùng: ${targetProfile.full_name} (${targetProfile.email})`,
      });
    } catch (err) {
      console.warn('[Auth] Log impersonate_start error:', err);
    }
  }, [user, isImpersonating, applyProfile]);

  const stopImpersonating = useCallback(async () => {
    if (!isImpersonating || !originalProfile) return;
    
    // Khôi phục profile gốc
    applyProfile(originalProfile);
    setOriginalProfile(null);
    setIsImpersonating(false);
    localStorage.removeItem('lscm_impersonated_id');
    
    // Log event (fire-and-forget)
    try {
      await supabase.rpc('log_auth_event', {
        p_user_id: user?.id,
        p_action: 'impersonate_stop',
        p_summary: `Dừng nhập vai`,
      });
    } catch (err) {
      console.warn('[Auth] Log impersonate_stop error:', err);
    }
  }, [user, isImpersonating, originalProfile, applyProfile]);

  const value: AuthContextValue = {
    user,
    session,
    profile,
    isLoading,
    isAuthenticated: !!user && !!session,
    authError,
    signIn,
    signInWithGoogle,
    signOut,
    refreshProfile,
    getRedirectPath,
    impersonateUser,
    stopImpersonating,
    isImpersonating,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
